// --- React & Redux
import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
// --- Actions & Hooks
import { useWindowsData } from "./hooks/useWindowsData";
import { useLoading } from "../../utils/hooks/useLoading";
import { useToasts } from "../../utils/hooks/useToasts";
import { usePermissions } from "../../utils/permissions";
import { useStoredItemsPerPageSync } from "tap/utils/hooks/useStoredItemsPerPageSync.js";
import { PAGINATION_STORAGE_KEYS } from "tap/utils/paginationPreferences.js";
// --- External UI Libraries
import Button from "@dtci/spellbook/Button";
import Typography from "@dtci/spellbook/Typography";
import { Col, Row } from "antd";
// --- Components
import { PaginationBar } from "../../components/PaginationBar";
import ToastNotificationProvider from "../../components/ToastNotifications";
import { WindowsGrid } from "./components/grid";
import { WindowsSearchForm } from "./components/search-form";
import { CreateWindowSequenceDrawer } from "./components/drawers/create";
import { EditWindowSequenceDrawer } from "./components/drawers/edit";
import { ViewWindowSequenceDrawer } from "./components/drawers/view";
import TwoRowsLayout from "./two-row";
// --- Utilities
import { tapApi } from "../../api";
import { exportToCsvWithHeaders } from "../../utils/exportToCsv";
import { getItemRange } from "../../utils/pagination";
import { parseSearchParams, updateSearchParams } from "../../utils/urlParams";
import {
  headers,
  rename,
  transformWindowsForExport,
} from "./utils/windowsExport";
// --- Config
import { WINDOWS_EXPORT_RANGE } from "./config/export";
// --- Styles
import "./styles.scss";

/**
 * Windows Page (Window Sequences)
 *
 * Features:
 * - Displays a paginated list of window sequences
 * - Provides search, export, and create functionality
 * - Handles loading and toast notifications
 * - Uses a two-row layout for search and grid
 *
 * @returns {JSX.Element}
 */
export const WindowsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fetchWindows, updateWindows, itemsPerPage, windowsRange } =
    useWindowsData();
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();
  const { isTapAdmin } = usePermissions();

  const { totalPages, currentPage, totalItems, pageSizeOptions } = useSelector(
    (s) => s.windows,
  );

  useStoredItemsPerPageSync({
    storageKey: PAGINATION_STORAGE_KEYS.windows,
    itemsPerPage,
    rangeKey: "windowsRange",
    onSync: updateWindows,
  });

  const handleChange = async (pageNumber) => {
    if (pageNumber === currentPage) return;

    const nextPageRange = getItemRange(pageNumber, itemsPerPage);
    const data = await fetchWindows(nextPageRange);

    if (data) {
      updateWindows({
        windowSequences: data?.content,
        totalPages: data?.totalPages,
        currentPage: data?.pageable?.pageNumber,
        windowsRange: nextPageRange,
        totalItems: data?.totalElements,
      });
    }
  };

  const handleExport = async () => {
    const data = await fetchWindows(WINDOWS_EXPORT_RANGE);

    if (data) {
      const flatRows = transformWindowsForExport(data?.content);
      exportToCsvWithHeaders(flatRows, headers, rename, "windows");
    }
  };

  const handlePageSizeChange = (newSize) => {
    const currentParams = parseSearchParams(location.search);
    updateSearchParams(location, navigate, {
      ...currentParams,
      pageSize: newSize,
    });
  };

  const handleRefresh = () => {
    updateWindows({ refreshWindows: true });
  };

  const handleCreateWindow = async () => {
    try {
      setLoadingTrue();
      const transactionalWindows = await tapApi.transactionalWindows();
      updateWindows({
        isCreateDrawerOpen: true,
        isEditDrawerOpen: false,
        isViewDrawerOpen: false,
        transactionalWindowsOptions: transactionalWindows || [],
      });
    } catch (error) {
      showErrorToast(error?.message || "Failed to open create form.");
    } finally {
      setLoadingFalse();
    }
  };

  return (
    <>
      <Row className="windows-page-header">
        <Col>
          <Typography variant="T50" style={{ marginLeft: 20 }}>
            Window Sequences
          </Typography>
        </Col>
        <Col className="windows-page-header-actions">
          {isTapAdmin && (
            <Button
              iconType="fa-solid fa-plus"
              labelText="Create"
              styleType="link"
              onClick={handleCreateWindow}
            >
              Create
            </Button>
          )}
          <Button
            iconType="fa-solid fa-arrow-down-to-line"
            labelText="Export"
            styleType="link"
            onClick={handleExport}
            disabled={totalItems === 0}
          >
            Export
          </Button>
          <Button
            iconType="fa-solid fa-rotate"
            labelText="Refresh"
            styleType="link"
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Col>
      </Row>
      <TwoRowsLayout>
        <main data-position="top">
          <WindowsSearchForm />
        </main>
        <main data-position="bottom">
          <WindowsGrid />
        </main>
      </TwoRowsLayout>
      <PaginationBar
        totalPages={totalPages || 0}
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handleChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={pageSizeOptions}
      />
      <CreateWindowSequenceDrawer />
      <EditWindowSequenceDrawer />
      <ViewWindowSequenceDrawer />
      <ToastNotificationProvider />
    </>
  );
};
